import { SvelteMap } from "svelte/reactivity";

// Coordonnées EPFL pour les modèles Word et Excel (03.11.2026) : l'annuaire
// public (search-api.epfl.ch, sans authentification, CORS ouvert) donne, à
// partir de l'e-mail de la session, la fonction, l'unité, le bureau et le
// téléphone ; la fiche de l'unité, son adresse postale (« EPFL P-EM DPO »,
// bâtiment, station), qui est celle du pied de page des modèles.
//
// Un document signé au nom d'une autre personne (un secrétariat qui écrit
// pour sa direction) : le modèle la nomme en signataire ou en expéditeur, et
// on cherche ses coordonnées de la même façon, par son nom.

const API = "https://search-api.epfl.ch/api";

/** Une accréditation : un rôle dans une unité. */
export interface Accred {
  acronym: string;
  unitName: string;
  position: string;
  phone: string;
  office: string;
}

export interface DirectoryPerson {
  name: string;
  email: string;
  accreds: Accred[];
}

/** Adresse postale et rattachement d'une unité. */
export interface DirectoryUnit {
  acronym: string;
  name: string;
  /** Niveau au-dessus de l'unité, sous l'École (faculté, vice-présidence…). */
  parentName: string;
  /** « EPFL P-EM DPO », « BS 248 (Bâtiment BS) », « Station 4 ». */
  sigle: string;
  bureau: string;
  station: string;
  url: string;
}

/** Ce qui remplit les champs d'un modèle : une personne dans une de ses unités. */
export interface Sender {
  person: DirectoryPerson;
  accred: Accred;
  unit: DirectoryUnit | null;
}

interface RawAccred {
  acronym?: string;
  name?: string;
  position?: string;
  phoneList?: string[];
  officeList?: string[];
  order?: number;
}
interface RawPerson {
  name?: string;
  firstname?: string;
  email?: string;
  accreds?: RawAccred[];
}

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url);
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

/** « +41216934531 » → « +41 21 693 45 31 ». */
export function formatPhone(raw: string): string {
  const m = /^\+41(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(raw.replace(/\s/g, ""));
  return m ? `+41 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : raw;
}

function toPerson(p: RawPerson): DirectoryPerson {
  const accreds = [...(p.accreds ?? [])]
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
    .map((a) => ({
      acronym: (a.acronym ?? "").trim(),
      unitName: (a.name ?? "").trim(),
      position: (a.position ?? "").trim(),
      phone: formatPhone((a.phoneList?.[0] ?? "").trim()),
      office: (a.officeList?.[0] ?? "").trim(),
    }))
    .filter((a) => a.acronym);
  return { name: [p.firstname, p.name].filter(Boolean).join(" "), email: p.email ?? "", accreds };
}

/** Sans accents ni casse, mots triés : « Dupont Jean » = « jean dupont ». */
/** Les mots d'un nom, sans accents ni titres, dans leur ordre. */
export function nameWords(name: string): string[] {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\b(prof|professeur|professeure|dr|docteur|madame|monsieur|mme|mr|m)\b\.?/g, " ")
    .split(/[\s.,'-]+/)
    .filter(Boolean);
}

export function nameKey(name: string): string {
  return nameWords(name).sort().join(" ");
}

export async function findPersonByEmail(email: string): Promise<DirectoryPerson | null> {
  const list = await getJson<RawPerson[]>(`${API}/ldap?q=${encodeURIComponent(email)}&hl=fr`);
  const hit = list?.find((p) => p.email?.toLowerCase() === email.toLowerCase());
  return hit ? toPerson(hit) : null;
}

/** Le nom écrit désigne-t-il cette personne ? Chaque mot écrit est un mot de
 *  son nom complet, ou son début : « Anna Fontcuberta » pour « Anna
 *  Fontcuberta i Morral », « J. Dupont » pour « Jean Dupont ». */
export function nameMatches(written: string, fullName: string): boolean {
  const words = nameKey(written).split(" ").filter(Boolean);
  const full = nameKey(fullName).split(" ").filter(Boolean);
  return words.length >= 2 && words.every((w) => full.some((f) => f === w || f.startsWith(w)));
}

/** Une seule personne de ce nom dans l'annuaire, sinon rien : mieux vaut un
 *  champ à compléter qu'un homonyme. Le nom complet exact l'emporte ; sinon
 *  le nom écrit peut n'en être qu'une partie (nom composé, second nom). */
export async function findPersonByName(name: string): Promise<DirectoryPerson | null> {
  const key = nameKey(name);
  if (!key.includes(" ")) return null;
  const list = (await getJson<RawPerson[]>(`${API}/ldap?q=${encodeURIComponent(key)}&hl=fr`)) ?? [];
  const full = (p: RawPerson) => `${p.firstname ?? ""} ${p.name ?? ""}`;
  const exact = list.filter((p) => nameKey(full(p)) === key);
  if (exact.length === 1) return toPerson(exact[0]);
  if (exact.length > 1) return null;
  const partial = list.filter((p) => nameMatches(name, full(p)));
  return partial.length === 1 ? toPerson(partial[0]) : null;
}

/** Distance d'édition (Levenshtein), pour les fautes de frappe. */
function editDistance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cur = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = cur;
    }
  }
  return row[b.length];
}

/** Forme sonore approximative d'un mot (sans accents, en minuscules) : les
 *  lettres qui se prononcent pareil confondues, h muet et lettres doublées
 *  retirés. « kuberta » et « cuberta », « von » et « fon » se rejoignent. */
export function phonetic(word: string): string {
  return word
    .replace(/ph/g, "f")
    .replace(/qu|ck|c|q/g, "k")
    .replace(/[vw]/g, "f")
    .replace(/y/g, "i")
    .replace(/z/g, "s")
    .replace(/h/g, "")
    .replace(/(.)\1+/g, "$1");
}

/** Ressemblance (0 à 1) entre le nom écrit et un nom complet : chaque mot
 *  écrit contre le mot le plus proche du nom (un début de mot compte entier). */
export function nameSimilarity(written: string, fullName: string): number {
  const words = nameWords(written);
  const full = nameWords(fullName);
  if (words.length === 0 || full.length === 0) return 0;
  // Écrit tel qu'entendu (« kuberta », « von ») : comparé aussi par le son.
  const raw = (a: string, b: string) => 1 - editDistance(a, b) / Math.max(a.length, b.length, 1);
  const ratio = (a: string, b: string) => Math.max(raw(a, b), raw(phonetic(a), phonetic(b)));
  const best = (w: string) => Math.max(...full.map((f) => (f.startsWith(w) ? 1 : ratio(w, f))));
  const byWord = words.reduce((sum, w) => sum + best(w), 0) / words.length;
  // Noms recollés : « anna von cuberta » contre « anna fontcuberta », une
  // suite de mots du nom complet, dans l'ordre (le nom écrit en omet souvent).
  const joined = words.join("");
  let byRun = 0;
  for (let a = 0; a < full.length; a++) {
    for (let b = a + 1; b <= full.length; b++) byRun = Math.max(byRun, ratio(joined, full.slice(a, b).join("")));
  }
  return Math.max(byWord, byRun);
}

/** Personnes dont le nom ressemble à `name`, la plus proche d'abord.
 *  L'annuaire ne cherche que par début de mot, sans tolérer de faute : on
 *  l'interroge sur chaque mot et sur ses premières lettres, puis on classe. */
export async function suggestPeople(name: string, max = 5): Promise<{ person: DirectoryPerson; score: number }[]> {
  const words = nameKey(name).split(" ").filter((w) => w.length >= 2);
  const queries = [...new Set([...words, ...words.filter((w) => w.length > 4).map((w) => w.slice(0, 4))])].slice(0, 6);
  const lists = await Promise.all(queries.map((q) => getJson<RawPerson[]>(`${API}/ldap?q=${encodeURIComponent(q)}&hl=fr`)));
  const seen = new Map<string, RawPerson>();
  for (const p of lists.flat()) if (p?.email && !seen.has(p.email)) seen.set(p.email, p);
  return [...seen.values()]
    .map((p) => ({ person: toPerson(p), score: nameSimilarity(name, `${p.firstname ?? ""} ${p.name ?? ""}`) }))
    .filter((c) => c.score >= 0.75)
    .sort((a, b) => b.score - a.score)
    .slice(0, max);
}

interface RawUnit {
  acronym?: string;
  name?: string;
  path?: { acronym?: string; name?: string }[];
  address?: string[];
  url?: string | null;
}

export async function findUnit(acronym: string): Promise<DirectoryUnit | null> {
  const u = await getJson<RawUnit>(`${API}/unit?acro=${encodeURIComponent(acronym)}&hl=fr`);
  if (!u || !u.acronym) return null;
  return unitFromRaw(u);
}

export function unitFromRaw(u: RawUnit): DirectoryUnit {
  // L'annuaire laisse parfois une espace en fin de nom.
  const address = (u.address ?? []).map((l) => l.trim());
  const stationAt = address.findIndex((l) => /^station\b/i.test(l));
  const path = u.path ?? [];
  return {
    acronym: (u.acronym ?? "").trim(),
    name: (u.name ?? "").trim(),
    parentName: path.length > 2 ? (path[1]?.name ?? "").trim() : "",
    sigle: address[0] ?? "",
    bureau: stationAt > 1 ? address.slice(1, stationAt).join(", ") : "",
    station: stationAt >= 0 ? address[stationAt] : "",
    url: u.url ?? "",
  };
}

export async function senderOf(person: DirectoryPerson, acronym?: string): Promise<Sender | null> {
  const accred = person.accreds.find((a) => a.acronym === acronym) ?? person.accreds[0];
  if (!accred) return null;
  return { person, accred, unit: await findUnit(accred.acronym) };
}

/** Les champs des modèles que l'annuaire remplit. `personal` : faux pour un
 *  signataire qu'on n'a pas trouvé, dont on garde l'unité de l'usager mais
 *  jamais son téléphone ni son e-mail. */
export function senderFields(s: Sender, personal = true): Map<string, string> {
  const f = new Map<string, string>();
  const unit = s.unit;
  f.set("unite", unit?.name || s.accred.unitName);
  f.set("sous-unite", "");
  if (unit?.parentName) f.set("faculte", unit.parentName);
  if (unit?.sigle) f.set("sigle", unit.sigle);
  if (unit?.bureau) f.set("bureau", unit.bureau);
  if (unit?.station) f.set("station", unit.station);
  f.set("site", unit?.url ? unit.url.replace(/^https?:\/\//, "").replace(/\/$/, "") : "www.epfl.ch");
  if (personal) {
    f.set("expediteur", s.person.name);
    f.set("signataire", [s.person.name, s.accred.position].filter(Boolean).join("\n"));
    if (s.accred.phone) f.set("telephone", s.accred.phone);
    if (s.person.email) f.set("email", s.person.email);
  }
  return f;
}

// === Signataires nommés par le modèle ========================================
// Cherchés une fois par nom ; l'aperçu (synchrone) lit le cache, qui le fait
// se redessiner à l'arrivée de la réponse.

const lookups = new SvelteMap<string, Sender | null>();
const pending = new Set<string>();

/** Le signataire déjà trouvé (null : introuvable, undefined : pas encore cherché). */
export function cachedSender(name: string): Sender | null | undefined {
  const key = nameKey(name);
  if (!lookups.has(key) && !pending.has(key)) void lookupSender(name);
  return lookups.get(key);
}

export async function lookupSender(name: string): Promise<Sender | null> {
  const key = nameKey(name);
  if (lookups.has(key)) return lookups.get(key)!;
  pending.add(key);
  const person = await findPersonByName(name);
  const sender = person ? await senderOf(person) : null;
  pending.delete(key);
  lookups.set(key, sender);
  return sender;
}

// === L'usager ================================================================
// Sa fiche, gardée dans le navigateur (relue au plus une fois par semaine), et
// l'accréditation qu'il a choisie pour ses documents.

const USER_KEY = "directoryUser";
const WEEK = 7 * 24 * 3600 * 1000;

interface StoredUser {
  email: string;
  acronym: string;
  sender: Sender;
  fetchedAt: number;
}

export const directory = $state<{ user: Sender | null; loading: boolean }>({ user: null, loading: false });

/** Après la connexion : la fiche de `email`, du cache ou de l'annuaire. */
export async function loadUser(email: string): Promise<void> {
  if (!email) return;
  const { getSetting } = await import("../db/settings");
  const stored = await getSetting<StoredUser>(USER_KEY);
  const same = stored && stored.email.toLowerCase() === email.toLowerCase();
  if (same) directory.user = stored.sender;
  if (same && Date.now() - stored.fetchedAt < WEEK) return;
  await refreshUser(email, same ? stored.acronym : undefined);
}

/** Relit l'annuaire ; `acronym` : l'accréditation à garder. */
export async function refreshUser(email: string, acronym?: string): Promise<void> {
  directory.loading = true;
  try {
    const person = await findPersonByEmail(email);
    const sender = person ? await senderOf(person, acronym) : null;
    if (!sender) return;
    directory.user = sender;
    const { setSetting } = await import("../db/settings");
    await setSetting<StoredUser>(USER_KEY, { email, acronym: sender.accred.acronym, sender: $state.snapshot(sender) as Sender, fetchedAt: Date.now() });
  } finally {
    directory.loading = false;
  }
}

/** Choix d'une autre accréditation dans le profil. */
export async function chooseAccred(acronym: string): Promise<void> {
  const user = directory.user;
  if (!user) return;
  await refreshUser(user.person.email, acronym);
}

// === Champs d'un fichier =====================================================

/** La personne au nom de qui le document est écrit, si le modèle la nomme. */
/** La personne au nom de qui le document est écrit, si ce n'est pas l'usager :
 *  le signataire d'abord (c'est son nom qu'on lit en bas de la lettre), puis
 *  l'expéditeur. Le modèle laisse souvent l'un des deux au nom de l'usager. */
export function namedSigner(fields: Map<string, string>, user: Sender | null): string {
  const names = [fields.get("signataire")?.split("\n")[0], fields.get("expediteur")].map((n) => (n ?? "").trim());
  return names.find((n) => n && !isUser(n, user)) ?? "";
}

/** Champs de l'annuaire, puis ceux du modèle par-dessus (un champ vide qu'il
 *  donne efface la ligne). `signer` : la personne nommée, si ce n'est pas
 *  l'usager (null : introuvable).
 *
 *  Écrit au nom d'une autre personne trouvée dans l'annuaire, ses coordonnées
 *  l'emportent sur ce que le modèle a recopié de l'usager (son unité, son
 *  nom en expéditeur) ; et une signature réduite à son nom prend sa fonction. */
export function withSender(model: Map<string, string>, user: Sender | null, signer?: Sender | null): Map<string, string> {
  const base = signer ? senderFields(signer) : user ? senderFields(user, signer === undefined) : new Map<string, string>();
  const out = new Map([...base, ...model]);
  if (signer && user) {
    const own = senderFields(user);
    for (const [key, value] of model) {
      // Recopié de l'usager, ou vidé pour « effacer ses coordonnées » : ce
      // sont celles de la personne trouvée qui vont là.
      if ((value ? own.get(key) === value : true) && base.get(key)) out.set(key, base.get(key)!);
    }
    const signature = model.get("signataire");
    // Le nom seul, ou suivi d'une fonction de remplissage (« Fonction »).
    const [first, ...rest] = (signature ?? "").split("\n");
    const filler = rest.every((l) => !l.trim() || /^\W*(fonction|titre|poste|function|title)\W*$/i.test(l.trim()));
    const named = (n: string) => nameKey(n) === nameKey(signer.person.name) || nameMatches(n, signer.person.name);
    if (signature !== undefined && filler && named(first)) {
      out.set("signataire", base.get("signataire")!);
    } else if (signature && named(first)) {
      // Fonction donnée par l'usager : gardée, sous le nom complet de l'annuaire.
      out.set("signataire", [signer.person.name, ...rest].join("\n"));
    }
    // « Anna Fontcuberta » : le nom complet de l'annuaire, « … i Morral ».
    const sender = model.get("expediteur");
    if (sender && named(sender)) out.set("expediteur", signer.person.name);
  }
  return out;
}

function isUser(name: string, user: Sender | null): boolean {
  return !name || (!!user && nameKey(name) === nameKey(user.person.name));
}

/** Pour le fichier : cherche la personne nommée si besoin. `unknown` : son
 *  nom, si l'annuaire ne la connaît pas (ou plusieurs personnes de ce nom). */
export async function resolveFields(model: Map<string, string>): Promise<{ fields: Map<string, string>; unknown: string }> {
  const user = directory.user;
  const name = namedSigner(model, user);
  if (!name) return { fields: withSender(model, user), unknown: "" };
  const signer = await lookupSender(name);
  return { fields: withSender(model, user, signer), unknown: signer ? "" : name };
}

/** Pour l'aperçu : ce qui est déjà connu (la recherche part en arrière-plan).
 *  `unknown` : la personne nommée, une fois la recherche faite sans succès. */
export function resolveFieldsNow(model: Map<string, string>): { fields: Map<string, string>; unknown: string } {
  const user = directory.user;
  const name = namedSigner(model, user);
  if (!name) return { fields: withSender(model, user), unknown: "" };
  const signer = cachedSender(name);
  // Recherche en cours : rien de personnel en attendant.
  return { fields: withSender(model, user, signer === undefined ? null : signer), unknown: signer === null ? name : "" };
}

/** Qui écrit, pour le prompt : le modèle peut signer et parler au nom de la
 *  bonne unité ; les coordonnées complètent les fichiers sans lui. */
export function senderContext(): string {
  const u = directory.user;
  if (!u) return "";
  const unit = u.unit?.name || u.accred.unitName;
  return (
    `Usager connecté (annuaire EPFL) : ${u.person.name}` +
    `${u.accred.position ? `, ${u.accred.position}` : ""}` +
    `${unit ? `, unité ${unit} (${u.accred.acronym})` : ""}` +
    `${u.unit?.parentName ? `, ${u.unit.parentName}` : ""}. ` +
    `Ses coordonnées complètent d'office l'en-tête, le pied de page et la signature des documents aux modèles EPFL : ne les recopie pas dans l'en-tête du bloc. ` +
    `Pour un document au nom d'une autre personne de l'EPFL, l'interface trouve elle-même son unité, sa fonction, son bureau et son téléphone dans l'annuaire EPFL à partir de son prénom et de son nom : tu n'as pas à les connaître ni à les demander. Si le nom n'y figure pas tel quel, l'interface cherche les noms proches et demande, au besoin, à l'usager de préciser.`
  );
}
